import { apiClient, apiBaseUrl } from "@/services/apiClient";
import type { VerifyResponse } from "@/services/authService";

export type EnterpriseRole = "enterprise_admin" | "enterprise_reviewer";

export interface Enterprise {
  id: string;
  slug: string;
  name: string;
  subdomain: string;
  logoUrl: string | null;
  logoUrlDark: string | null;
  primaryColor: string | null;
  primaryColorDark: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseMember {
  id: string;
  userId: string;
  role: EnterpriseRole;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  acceptedAt: string | null;
  invitedAt: string | null;
}

export interface EnterpriseInvite {
  id: string;
  enterpriseId: string;
  email: string;
  role: EnterpriseRole;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
}

export interface VerifyEnterpriseInviteResult {
  inviteId: string;
  enterpriseId: string;
  enterpriseName: string;
  subdomain: string;
  logoUrl: string | null;
  logoUrlDark: string | null;
  primaryColor: string | null;
  email: string;
  role: EnterpriseRole;
  expiresAt: string;
}

export interface CreateEnterprisePayload {
  name: string;
  subdomain: string;
  logoUrl?: string;
  logoUrlDark?: string;
  primaryColor?: string;
  primaryColorDark?: string;
  adminEmail: string;
}

export interface UpdateEnterprisePayload {
  name?: string;
  subdomain?: string;
  logoUrl?: string | null;
  logoUrlDark?: string | null;
  primaryColor?: string | null;
  primaryColorDark?: string | null;
  isActive?: boolean;
}

export function listEnterprises(): Promise<Enterprise[]> {
  return apiClient.get<Enterprise[]>("/root/enterprises").then((res) => res.data);
}

export function getEnterprise(id: string): Promise<Enterprise> {
  return apiClient.get<Enterprise>(`/root/enterprises/${id}`).then((res) => res.data);
}

export function createEnterprise(payload: CreateEnterprisePayload): Promise<Enterprise> {
  return apiClient.post<Enterprise>("/root/enterprises", payload).then((res) => res.data);
}

export function updateEnterprise(
  id: string,
  payload: UpdateEnterprisePayload,
): Promise<Enterprise> {
  return apiClient.patch<Enterprise>(`/root/enterprises/${id}`, payload).then((res) => res.data);
}

export function uploadEnterpriseLogo(
  enterpriseId: string,
  file: File,
): Promise<{ logoUrl: string }> {
  const formData = new FormData();
  formData.append("logo", file);
  return apiClient
    .post<{ logoUrl: string }>(`/root/enterprises/${enterpriseId}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function listEnterpriseMembers(enterpriseId: string): Promise<EnterpriseMember[]> {
  return apiClient
    .get<EnterpriseMember[]>(`/root/enterprises/${enterpriseId}/members`)
    .then((res) => res.data);
}

export function revokeEnterpriseMember(
  enterpriseId: string,
  membershipId: string,
): Promise<void> {
  return apiClient
    .delete(`/root/enterprises/${enterpriseId}/members/${membershipId}`)
    .then(() => undefined);
}

export function listEnterpriseInvites(enterpriseId: string): Promise<EnterpriseInvite[]> {
  return apiClient
    .get<EnterpriseInvite[]>(`/root/enterprises/${enterpriseId}/invites`)
    .then((res) => res.data);
}

export function inviteEnterpriseMember(
  enterpriseId: string,
  payload: { email: string; role: EnterpriseRole },
): Promise<{ inviteId: string; expiresAt: string }> {
  return apiClient
    .post<{ inviteId: string; expiresAt: string }>(
      `/root/enterprises/${enterpriseId}/invites`,
      payload,
    )
    .then((res) => res.data);
}

export function revokeEnterpriseInvite(
  enterpriseId: string,
  inviteId: string,
): Promise<void> {
  return apiClient
    .delete(`/root/enterprises/${enterpriseId}/invites/${inviteId}`)
    .then(() => undefined);
}

// ── Re-exports from the shared graph package ─────────────────────────────

export type {
  WizardFieldOption,
  WizardFieldDef,
  PreconditionFieldOption,
  PreconditionFieldDef,
  PreconditionRuleMaxPercentage,
  PreconditionRuleConditional,
  PreconditionRule,
  PreconditionCondition,
  PreconditionExpr,
  PreconditionOp,
  PreconditionDef,
  PreconditionEvalResult,
  WorkflowActor,
  GraphState,
  GraphStepCompletionRecord,
  RateTableEntry,
  InstallmentTier,
  PricingFormula,
  ProductNodeType,
  ProductNode,
  ProductEdge,
  ProductGraph,
} from "@gestionalo/enterprise-graph";
export { evaluatePrecondition } from "@gestionalo/enterprise-graph";

// ── Product types ────────────────────────────────────────────────────────

import type { ProductGraph } from "@gestionalo/enterprise-graph";

export interface EnterpriseProduct {
  id: string;
  enterpriseId: string;
  slug: string;
  displayName: string;
  description: string;
  category: string;
  amountMinor: number;
  currency: string;
  graph: ProductGraph | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEnterpriseProductPayload {
  displayName: string;
  description?: string;
  category: string;
  amountMinor: number;
  currency?: string;
  graph?: ProductGraph | null;
  isActive?: boolean;
  sortOrder?: number;
  slug?: string;
}

export type UpdateEnterpriseProductPayload = Partial<CreateEnterpriseProductPayload>;

export function listEnterpriseProducts(
  enterpriseId: string,
): Promise<EnterpriseProduct[]> {
  return apiClient
    .get<EnterpriseProduct[]>(`/root/enterprises/${enterpriseId}/products`)
    .then((res) => res.data);
}

export function getEnterpriseProduct(
  enterpriseId: string,
  productId: string,
): Promise<EnterpriseProduct> {
  return apiClient
    .get<EnterpriseProduct>(`/root/enterprises/${enterpriseId}/products/${productId}`)
    .then((res) => res.data);
}

export function createEnterpriseProduct(
  enterpriseId: string,
  payload: CreateEnterpriseProductPayload,
): Promise<EnterpriseProduct> {
  return apiClient
    .post<EnterpriseProduct>(`/root/enterprises/${enterpriseId}/products`, payload)
    .then((res) => res.data);
}

export function updateEnterpriseProduct(
  enterpriseId: string,
  productId: string,
  payload: UpdateEnterpriseProductPayload,
): Promise<EnterpriseProduct> {
  return apiClient
    .patch<EnterpriseProduct>(`/root/enterprises/${enterpriseId}/products/${productId}`, payload)
    .then((res) => res.data);
}

export function deleteEnterpriseProduct(
  enterpriseId: string,
  productId: string,
): Promise<void> {
  return apiClient
    .delete(`/root/enterprises/${enterpriseId}/products/${productId}`)
    .then(() => undefined);
}

export function enterpriseLogoUrl(enterpriseId: string): string {
  return `${apiBaseUrl}/enterprises/${enterpriseId}/logo`;
}

export function enterpriseDarkLogoUrl(enterpriseId: string): string {
  return `${apiBaseUrl}/enterprises/${enterpriseId}/logo-dark`;
}

export function uploadEnterpriseDarkLogo(
  enterpriseId: string,
  file: File,
): Promise<{ logoUrlDark: string }> {
  const formData = new FormData();
  formData.append("logo", file);
  return apiClient
    .post<{ logoUrlDark: string }>(`/root/enterprises/${enterpriseId}/logo-dark`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function verifyEnterpriseInvite(
  token: string,
): Promise<VerifyEnterpriseInviteResult> {
  return apiClient
    .get<VerifyEnterpriseInviteResult>("/enterprise-invites/verify", {
      params: { token },
    })
    .then((res) => res.data);
}

export function acceptEnterpriseInvite(token: string): Promise<void> {
  return apiClient
    .post("/enterprise-invites/accept", { token })
    .then(() => undefined);
}

export function acceptEnterpriseInviteWithRegistration(params: {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/enterprise-invites/accept-with-registration", params)
    .then((res) => res.data);
}

export function uploadProductTemplate(
  enterpriseId: string,
  productId: string,
  file: File,
): Promise<{ url: string; filename: string }> {
  const formData = new FormData();
  formData.append('template', file);
  return apiClient
    .post<{ url: string; filename: string }>(
      `/root/enterprises/${enterpriseId}/products/${productId}/template`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    .then((res) => res.data);
}
